import { SessionRecord, AnalysisResult, TeacherVideo, TeacherProfile, IntegrityPack } from '../types';
import { api } from './api';

const DB_NAME = 'GuitarMatchPlayDB';
const DB_VERSION = 6;
const STORE_SESSIONS = 'sessions';
const STORE_AUDIO = 'audio_blobs';
const STORE_TEACHER_VIDEOS = 'teacher_videos';
const STORE_TEACHER_PROFILE = 'teacher_profile';

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        db.createObjectStore(STORE_SESSIONS, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_AUDIO)) {
        db.createObjectStore(STORE_AUDIO);
      }
      if (!db.objectStoreNames.contains(STORE_TEACHER_VIDEOS)) {
        db.createObjectStore(STORE_TEACHER_VIDEOS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_TEACHER_PROFILE)) {
        db.createObjectStore(STORE_TEACHER_PROFILE, { keyPath: 'id' });
      }
    };
  });
};

export const migrateLegacySessions = async (): Promise<void> => {
  try {
    const db = await openDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction([STORE_SESSIONS, STORE_AUDIO], 'readwrite');
      const sessionStore = tx.objectStore(STORE_SESSIONS);
      const audioStore = tx.objectStore(STORE_AUDIO);
      const request = sessionStore.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const session = cursor.value;
          if (session.stuAudioBlob) {
            audioStore.put(session.stuAudioBlob, session.id);
            delete session.stuAudioBlob;
            cursor.update(session);
          }
          cursor.continue();
        }
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // ignore
  }
};

export const enforceRetentionPolicy = async (): Promise<void> => {
  try {
    const db = await openDB();
    const CUTOFF_MS = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const limitDate = now - CUTOFF_MS;

    await new Promise<void>((resolve) => {
      const tx = db.transaction([STORE_SESSIONS, STORE_AUDIO], 'readwrite');
      const sessionStore = tx.objectStore(STORE_SESSIONS);
      const audioStore = tx.objectStore(STORE_AUDIO);

      const request = sessionStore.openCursor();
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const session: SessionRecord = cursor.value;
          const sessionDate = new Date(session.date).getTime();

          if (sessionDate < limitDate && !session.isFavorite && !session.audioExpired) {
            audioStore.delete(session.id);
            session.audioExpired = true;

            if ((session as any).stuAudioBlob) delete (session as any).stuAudioBlob;
            cursor.update(session);
          }
          cursor.continue();
        }
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch (e) {
    console.warn('[STORAGE] Retention Policy Error', e);
  }
};

export const toggleSessionFavorite = async (sessionId: number): Promise<boolean> => {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction([STORE_SESSIONS], 'readwrite');
      const store = tx.objectStore(STORE_SESSIONS);
      const req = store.get(sessionId);

      req.onsuccess = () => {
        const session = req.result;
        if (session) {
          session.isFavorite = !session.isFavorite;
          store.put(session);
        }
      };

      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
};

const decimate = (arr: any[], factor: number) => {
  if (!arr || !Array.isArray(arr)) return [];
  return arr.filter((_, i) => i % factor === 0);
};

const compressSessionData = (fullResult: AnalysisResult): AnalysisResult => {
  const compressed = JSON.parse(JSON.stringify(fullResult));
  if (compressed.harmony?.chromaStu) {
    compressed.harmony.chromaStu = decimate(compressed.harmony.chromaStu, 5);
  }
  if (compressed.harmony?.chromaRef) {
    compressed.harmony.chromaRef = decimate(compressed.harmony.chromaRef, 5);
  }
  return compressed;
};

function stripEphemeralAlignedUrls(result: AnalysisResult): AnalysisResult {
  const clone = JSON.parse(JSON.stringify(result)) as AnalysisResult;
  if ((clone as any).alignedUrls) delete (clone as any).alignedUrls;
  return clone;
}

export const saveSession = async (
  refFile: File | null,
  stuFile: File,
  analysisResult: AnalysisResult,
  options?: { ephemeral?: boolean }
): Promise<boolean> => {
  try {
    if (options?.ephemeral) return true;

    const db = await openDB();
    const truth = analysisResult.session.truth_report;

    const integrityMetadata: IntegrityPack = {
      status: (analysisResult.session.isUnmeasurable ? 'invalid' : 'valid') as 'invalid' | 'valid',
      integrity_pass: truth?.status === 'OK',
      score: analysisResult.session.score,
      metrics: { rms_db: analysisResult.session.metrics.debug?.rms_db || -240 }
    };

    const stripped = stripEphemeralAlignedUrls(analysisResult);
    const lightweightResult = compressSessionData(stripped);

    const record: Omit<SessionRecord, 'id'> = {
      date: new Date().toISOString(),
      title: stuFile.name
        ? stuFile.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
        : 'New Session',
      score: analysisResult.session.score,
      rank: analysisResult.session.rank,
      bpm: analysisResult.session.bpm,
      integrity_pack: integrityMetadata,
      result: lightweightResult,
      isFavorite: false,
      audioExpired: false
    };

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([STORE_SESSIONS, STORE_AUDIO], 'readwrite');
      const reqAdd = tx.objectStore(STORE_SESSIONS).add(record);

      reqAdd.onsuccess = (e) => {
        const newId = (e.target as IDBRequest).result;
        tx.objectStore(STORE_AUDIO).put(stuFile, newId);
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    return true;
  } catch (e) {
    console.error('[STORAGE] saveSession error:', e);
    return false;
  }
};

export const getSessionAudio = async (sessionId: number): Promise<File | undefined> => {
  try {
    const db = await openDB();
    return new Promise<File | undefined>((resolve) => {
      const tx = db.transaction([STORE_AUDIO], 'readonly');
      const req = tx.objectStore(STORE_AUDIO).get(sessionId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(undefined);
    });
  } catch {
    return undefined;
  }
};

export const getHistory = async (): Promise<SessionRecord[]> => {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_SESSIONS], 'readonly');
      const request = transaction.objectStore(STORE_SESSIONS).getAll();

      request.onsuccess = () => {
        const optimized = (request.result || []).map((r: SessionRecord) => {
          const { stuAudioBlob, ...lightweight } = r as any;
          return lightweight as SessionRecord;
        });
        resolve(optimized);
      };

      request.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
};

export const deleteSession = async (id: number): Promise<boolean> => {
  try {
    const db = await openDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction([STORE_SESSIONS, STORE_AUDIO], 'readwrite');
      tx.objectStore(STORE_SESSIONS).delete(id);
      tx.objectStore(STORE_AUDIO).delete(id);
      tx.oncomplete = () => resolve();
    });

    api.delete(`/sessions/${id}`).catch(() => {});
    return true;
  } catch {
    return false;
  }
};

export const saveTeacherVideo = async (video: TeacherVideo): Promise<boolean> => {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction([STORE_TEACHER_VIDEOS], 'readwrite');
      tx.objectStore(STORE_TEACHER_VIDEOS).put(video);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
};

export const getTeacherVideos = async (): Promise<TeacherVideo[]> => {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const request = db
        .transaction([STORE_TEACHER_VIDEOS], 'readonly')
        .objectStore(STORE_TEACHER_VIDEOS)
        .getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
};

export const deleteTeacherVideo = async (id: string): Promise<boolean> => {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction([STORE_TEACHER_VIDEOS], 'readwrite');
      tx.objectStore(STORE_TEACHER_VIDEOS).delete(id);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
};

export const saveTeacherProfile = async (profile: TeacherProfile): Promise<void> => {
  const db = await openDB();
  db.transaction([STORE_TEACHER_PROFILE], 'readwrite')
    .objectStore(STORE_TEACHER_PROFILE)
    .put({ ...profile, id: 'main_teacher' });
};

export const getTeacherProfile = async (): Promise<TeacherProfile | null> => {
  const db = await openDB();
  return new Promise((resolve) => {
    const request = db
      .transaction([STORE_TEACHER_PROFILE], 'readonly')
      .objectStore(STORE_TEACHER_PROFILE)
      .get('main_teacher');

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => resolve(null);
  });
};

/**
 * ✅ Stats utilisateur (NO-SIM)
 * Contrat attendu (contracts/social.ts):
 * { avgScore, totalMatches, wins, bestRank, levelXP, timingSkill, soulSkill, cleanSkill }
 *
 * On renvoie aussi timing/soul/clean en bonus (compat legacy).
 */
export const computeUserStatsFromHistory = async (): Promise<{
  avgScore: number;
  totalMatches: number;
  wins: number;
  bestRank: string;
  levelXP: number;
  timingSkill: number;
  soulSkill: number;
  cleanSkill: number;

  // legacy
  timing: number;
  soul: number;
  clean: number;
}> => {
  const history = await getHistory();

  if (history.length === 0) {
    return {
      avgScore: 0,
      totalMatches: 0,
      wins: 0,
      bestRank: '—',
      levelXP: 0,
      timingSkill: 0,
      soulSkill: 0,
      cleanSkill: 0,
      timing: 0,
      soul: 0,
      clean: 0
    };
  }

  let sumTiming = 0;
  let sumSoul = 0;
  let sumClean = 0;
  let sumScore = 0;
  let validCount = 0;

  let wins = 0;
  let bestScore = -1;
  let bestRank = '—';

  history.forEach((session) => {
    const score = session.score ?? 0;
    if (score > bestScore) {
      bestScore = score;
      bestRank = session.rank ?? '—';
    }

    // ✅ définition "wins" simple et défendable : score >= 90 (ou rank STUDIO MASTER)
    if (score >= 90 || session.rank === 'STUDIO MASTER') wins++;

    const m = session.result?.session?.metrics;
    if (m) {
      sumTiming += m.rhythm || 0;

      // "soul" = moyenne nuance + articulation (tu avais ça)
      sumSoul += ((m.nuance || 0) + (m.articulation || 0)) / 2;

      sumClean += m.cleanliness || 0;
      sumScore += score;
      validCount++;
    }
  });

  if (validCount === 0) {
    return {
      avgScore: 0,
      totalMatches: history.length,
      wins: 0,
      bestRank,
      levelXP: 0,
      timingSkill: 0,
      soulSkill: 0,
      cleanSkill: 0,
      timing: 0,
      soul: 0,
      clean: 0
    };
  }

  const timingSkill = Math.round(sumTiming / validCount);
  const soulSkill = Math.round(sumSoul / validCount);
  const cleanSkill = Math.round(sumClean / validCount);
  const avgScore = Math.round(sumScore / validCount);

  // ✅ XP NO-SIM: basé uniquement sur l'historique
  // Proposition simple: total de scores / 2, plafonné à 99999 (évite inflation)
  const rawXP = Math.round(sumScore / 2);
  const levelXP = Math.max(0, Math.min(rawXP, 99999));

  return {
    avgScore,
    totalMatches: history.length,
    wins,
    bestRank,
    levelXP,
    timingSkill,
    soulSkill,
    cleanSkill,

    // legacy mirror
    timing: timingSkill,
    soul: soulSkill,
    clean: cleanSkill
  };
};
